import type { 
  Diagnosis, 
  MedicalHistoryItem, 
  FamilyHistoryItem, 
  InsuranceProvider, 
  InsuranceClaim, 
  TreatmentPlan 
} from "@/shared/types";

export const sampleDiagnoses: Diagnosis[] = [
  {
    id: "diag-1",
    name: "Type 2 Diabetes Mellitus",
    icdCode: "E11.9",
    diagnosedDate: "2023-06-15",
    diagnosedBy: "Dr. Sarah Chen",
    status: "chronic",
    severity: "moderate",
    description: "Type 2 diabetes is a chronic condition that affects the way your body metabolizes sugar (glucose). With type 2 diabetes, your body either resists the effects of insulin or doesn't produce enough insulin to maintain normal glucose levels.",
    plainLanguageSummary: "Your body has trouble using sugar from food for energy. This means sugar builds up in your blood instead of being used by your cells. You can manage this with lifestyle changes and medication.",
    relatedRecords: ["rec-1", "rec-2"],
  },
  {
    id: "diag-2",
    name: "Essential Hypertension",
    icdCode: "I10",
    diagnosedDate: "2022-03-20",
    diagnosedBy: "Dr. Michael Rodriguez",
    status: "active",
    severity: "mild",
    description: "Essential hypertension is high blood pressure that develops over time with no identifiable cause. It's the most common type of high blood pressure.",
    plainLanguageSummary: "Your blood pushes too hard against your artery walls. Think of it like water pressure in a hose being too high. Over time, this can damage your heart and blood vessels, but it's very manageable with medication and healthy habits.",
    relatedRecords: ["rec-3"],
  },
  {
    id: "diag-3",
    name: "Seasonal Allergic Rhinitis",
    icdCode: "J30.2",
    diagnosedDate: "2020-04-10",
    diagnosedBy: "Dr. Emily Watson",
    status: "active",
    severity: "mild",
    description: "Seasonal allergic rhinitis, commonly known as hay fever, is an allergic response to outdoor or indoor allergens, such as pollen, dust mites, or pet dander.",
    plainLanguageSummary: "Your immune system overreacts to things like pollen or dust, causing sneezing, runny nose, and itchy eyes during certain seasons. It's uncomfortable but not dangerous, and can be managed with antihistamines.",
    relatedRecords: [],
  },
];

export const sampleMedicalHistory: MedicalHistoryItem[] = [
  {
    id: "mh-1",
    type: "surgery",
    title: "Appendectomy",
    date: "2018-08-12",
    provider: "Dr. James Wilson",
    facility: "City General Hospital",
    notes: "Laparoscopic appendectomy performed due to acute appendicitis. No complications during surgery.",
    outcome: "Full recovery within 2 weeks",
  },
  {
    id: "mh-2",
    type: "hospitalization",
    title: "Pneumonia Treatment",
    date: "2021-01-05",
    provider: "Dr. Lisa Park",
    facility: "Memorial Medical Center",
    notes: "Admitted for community-acquired pneumonia. Treated with IV antibiotics for 3 days.",
    outcome: "Discharged with oral antibiotics, full recovery",
  },
  {
    id: "mh-3",
    type: "procedure",
    title: "Colonoscopy",
    date: "2024-02-20",
    provider: "Dr. Robert Kim",
    facility: "Digestive Health Center",
    notes: "Routine screening colonoscopy. Two small polyps removed and sent for biopsy.",
    outcome: "Benign polyps, follow-up in 5 years",
  },
  {
    id: "mh-4",
    type: "allergy",
    title: "Penicillin Allergy",
    date: "2010-05-15",
    provider: "Dr. Susan Miller",
    facility: "Primary Care Associates",
    notes: "Developed hives and mild throat swelling after taking amoxicillin. Allergist confirmed penicillin allergy.",
    outcome: "Avoid all penicillin-based antibiotics",
  },
  {
    id: "mh-5",
    type: "condition",
    title: "Childhood Asthma",
    date: "1995-09-01",
    provider: "Dr. Karen Thompson",
    facility: "Children's Medical Group",
    notes: "Diagnosed with mild persistent asthma. Used inhaler until age 16.",
    outcome: "Outgrew symptoms, no current treatment needed",
  },
];

export const sampleFamilyHistory: FamilyHistoryItem[] = [
  {
    id: "fh-1",
    relation: "Father",
    condition: "Type 2 Diabetes",
    ageAtOnset: 55,
    status: "living",
    notes: "Well-controlled with medication",
  },
  {
    id: "fh-2",
    relation: "Mother",
    condition: "Breast Cancer",
    ageAtOnset: 62,
    status: "living",
    notes: "Diagnosed 2019, in remission after treatment",
  },
  {
    id: "fh-3",
    relation: "Paternal Grandfather",
    condition: "Heart Disease",
    ageAtOnset: 65,
    status: "deceased",
    notes: "Had bypass surgery at 65, passed away at 78",
  },
  {
    id: "fh-4",
    relation: "Maternal Grandmother",
    condition: "Alzheimer's Disease",
    ageAtOnset: 75,
    status: "deceased",
    notes: "Developed symptoms at 75",
  },
  {
    id: "fh-5",
    relation: "Brother",
    condition: "High Cholesterol",
    ageAtOnset: 35,
    status: "living",
    notes: "Managing with diet and statins",
  },
];

export const sampleInsuranceProviders: InsuranceProvider[] = [
  {
    id: "ins-1",
    name: "Blue Cross Blue Shield",
    planType: "PPO",
    memberId: "XYZ123456789",
    groupNumber: "GRP-98765",
    policyHolder: "Self",
    effectiveDate: "2024-01-01",
    copay: {
      primaryCare: 25,
      specialist: 45,
      emergency: 150,
      prescription: 15,
    },
    deductible: {
      individual: 1500,
      family: 3000,
      met: 875,
    },
    outOfPocketMax: {
      individual: 6000,
      family: 12000,
      met: 1250,
    },
    contactPhone: "1-800-555-0123",
    contactEmail: "members@bcbs.example.com",
    isActive: true,
  },
  {
    id: "ins-2",
    name: "Delta Dental",
    planType: "PPO",
    memberId: "DD-987654321",
    groupNumber: "DENTAL-456",
    policyHolder: "Self",
    effectiveDate: "2024-01-01",
    copay: {
      primaryCare: 0,
      specialist: 0,
      emergency: 50,
      prescription: 0,
    },
    deductible: {
      individual: 50,
      family: 150,
      met: 50,
    },
    outOfPocketMax: {
      individual: 1500,
      family: 3000,
      met: 320,
    },
    contactPhone: "1-800-555-0456",
    isActive: true,
  },
];

export const sampleClaims: InsuranceClaim[] = [
  {
    id: "claim-1",
    providerId: "ins-1",
    serviceDate: "2024-03-15",
    submittedDate: "2024-03-18",
    claimNumber: "CLM-2024-001234",
    serviceType: "Office Visit - Primary Care",
    providerName: "Dr. Sarah Chen",
    billedAmount: 250,
    allowedAmount: 175,
    paidAmount: 150,
    patientResponsibility: 25,
    status: "approved",
    notes: "Annual wellness visit",
  },
  {
    id: "claim-2",
    providerId: "ins-1",
    serviceDate: "2024-03-20",
    submittedDate: "2024-03-22",
    claimNumber: "CLM-2024-001289",
    serviceType: "Laboratory Services",
    providerName: "Quest Diagnostics",
    billedAmount: 450,
    allowedAmount: 280,
    paidAmount: 224,
    patientResponsibility: 56,
    status: "approved",
    notes: "Comprehensive metabolic panel, lipid panel, HbA1c",
  },
  {
    id: "claim-3",
    providerId: "ins-1",
    serviceDate: "2024-02-28",
    submittedDate: "2024-03-01",
    claimNumber: "CLM-2024-000987",
    serviceType: "Specialist Visit",
    providerName: "Dr. Michael Rodriguez",
    billedAmount: 350,
    allowedAmount: 220,
    paidAmount: 175,
    patientResponsibility: 45,
    status: "approved",
    notes: "Cardiology consultation",
  },
  {
    id: "claim-4",
    providerId: "ins-1",
    serviceDate: "2024-03-25",
    submittedDate: "2024-03-26",
    claimNumber: "CLM-2024-001456",
    serviceType: "Prescription",
    providerName: "CVS Pharmacy",
    billedAmount: 125,
    allowedAmount: 95,
    paidAmount: 80,
    patientResponsibility: 15,
    status: "pending",
    notes: "Metformin 90-day supply",
  },
  {
    id: "claim-5",
    providerId: "ins-1",
    serviceDate: "2024-01-10",
    submittedDate: "2024-01-12",
    claimNumber: "CLM-2024-000123",
    serviceType: "Diagnostic Imaging",
    providerName: "Radiology Associates",
    billedAmount: 1200,
    allowedAmount: 650,
    paidAmount: 0,
    patientResponsibility: 650,
    status: "denied",
    notes: "Prior authorization not obtained - appeal in progress",
  },
];

export const sampleTreatmentPlans: TreatmentPlan[] = [
  {
    id: "tp-1",
    diagnosisId: "diag-1",
    diagnosisName: "Type 2 Diabetes Mellitus",
    createdDate: "2023-06-20",
    createdBy: "Dr. Sarah Chen",
    status: "active",
    goals: [
      {
        id: "goal-1",
        description: "Achieve HbA1c below 7.0%",
        targetDate: "2024-06-15",
        status: "in_progress",
        progress: 70,
      },
      {
        id: "goal-2",
        description: "Lose 15 pounds to reach healthy BMI",
        targetDate: "2024-09-01",
        status: "in_progress",
        progress: 45,
      },
      {
        id: "goal-3",
        description: "Establish regular exercise routine (150 min/week)",
        targetDate: "2024-03-01",
        status: "achieved",
        progress: 100,
      },
    ],
    medications: [
      {
        name: "Metformin",
        dosage: "1000mg",
        frequency: "Twice daily with meals",
        duration: "Ongoing",
        purpose: "Helps your body respond better to insulin and reduces sugar production in the liver",
      },
      {
        name: "Jardiance",
        dosage: "10mg",
        frequency: "Once daily in the morning",
        duration: "Ongoing",
        purpose: "Helps remove excess sugar through urine and protects your heart and kidneys",
      },
    ],
    appointments: [
      {
        type: "Endocrinology Follow-up",
        frequency: "Every 3 months",
        nextDate: "2024-06-15",
        provider: "Dr. Sarah Chen",
      },
      {
        type: "Lab Work (HbA1c, Lipids)",
        frequency: "Every 3 months",
        nextDate: "2024-06-01",
        provider: "Quest Diagnostics",
      },
      {
        type: "Eye Exam",
        frequency: "Annually",
        nextDate: "2024-12-01",
        provider: "Dr. Vision Care",
      },
    ],
    lifestyle: [
      "Follow low-carb Mediterranean diet - aim for 45-60g carbs per meal",
      "Check blood sugar before meals and at bedtime",
      "Walk 30 minutes daily or 150 minutes of moderate exercise per week",
      "Stay hydrated - drink at least 8 glasses of water daily",
      "Get 7-8 hours of sleep each night",
    ],
    notes: "Patient is responding well to treatment. Consider adding GLP-1 agonist if HbA1c doesn't reach target by next visit.",
    nextReviewDate: "2024-06-15",
  },
  {
    id: "tp-2",
    diagnosisId: "diag-2",
    diagnosisName: "Essential Hypertension",
    createdDate: "2022-03-25",
    createdBy: "Dr. Michael Rodriguez",
    status: "active",
    goals: [
      {
        id: "goal-4",
        description: "Maintain blood pressure below 130/80 mmHg",
        targetDate: "2024-12-31",
        status: "in_progress",
        progress: 85,
      },
      {
        id: "goal-5",
        description: "Reduce sodium intake to less than 2300mg daily",
        targetDate: "2024-06-01",
        status: "achieved",
        progress: 100,
      },
    ],
    medications: [
      {
        name: "Lisinopril",
        dosage: "10mg",
        frequency: "Once daily",
        duration: "Ongoing",
        purpose: "Relaxes blood vessels to lower blood pressure and reduce strain on your heart",
      },
    ],
    appointments: [
      {
        type: "Cardiology Follow-up",
        frequency: "Every 6 months",
        nextDate: "2024-08-28",
        provider: "Dr. Michael Rodriguez",
      },
      {
        type: "Blood Pressure Check",
        frequency: "Monthly",
        nextDate: "2024-04-15",
        provider: "Primary Care Office",
      },
    ],
    lifestyle: [
      "Follow DASH diet - emphasize fruits, vegetables, whole grains, lean proteins",
      "Limit sodium to less than 2300mg per day",
      "Monitor blood pressure at home twice daily and log results",
      "Limit alcohol to 1 drink per day",
      "Manage stress through meditation or relaxation techniques",
    ],
    notes: "Blood pressure well-controlled on current regimen. Continue monitoring and lifestyle modifications.",
    nextReviewDate: "2024-08-28",
  },
];
